import { useQuery } from '@tanstack/react-query';

import { STALE_TIME } from '~/domains/contest/constants/query';
import {
  getPicksForEntries,
  getUserEntriesForContest,
} from '~/domains/contest/domains/services/entries.service.api';
import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import { useDefaultSlate } from '~/domains/games/hooks/useDefaultSlate';

type UseMyContestEntriesProps = {
  contestId?: string;
  scheduleId?: string;
  userId?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
};

const useMyContestEntries = ({
  contestId,
  scheduleId,
  userId,
  pageSize = 20,
  page = 1,
  enabled = true,
}: UseMyContestEntriesProps) => {
  // 1. Get Contest Slates - we will use the fist one
  const {
    slate: defaultSlate,
    isLoading: isLoadingContestSlates,
    isError: isErrorContestSlates,
  } = useDefaultSlate(scheduleId, {
    enabled,
    staleTime: STALE_TIME,
  });
  const slateId = defaultSlate?.id;
  const slateStatus = defaultSlate?.status;

  // 2. Get user entries current contest
  const limit = pageSize;
  const offset = (page - 1) * pageSize;
  const {
    data: entriesForCurrentUser,
    isLoading: isLoadingEntriesForCurrentUser,
    isError: isErrorEntriesForCurrentUser,
  } = useQuery({
    queryKey: ['entriesForCurrentUser', contestId, userId, limit, offset],
    staleTime: STALE_TIME,
    enabled: enabled && !!contestId && !!userId,
    queryFn: () => getUserEntriesForContest(contestId, userId, limit, offset),
  });

  // 3. Get picks for entries (only returns entries if they have picks)
  const {
    data: entriesToPicks,
    isLoading: isLoadingEntryToPicks,
    isError: isErrorEntryToPicks,
  } = useQuery({
    queryKey: ['entriesToPicks', contestId, slateId],
    enabled: enabled && !!contestId && !!slateId,
    staleTime: STALE_TIME,
    queryFn: async () => {
      const entriesWithPicks = await getPicksForEntries(contestId, slateId);
      return entriesWithPicks.reduce((acc, entry) => {
        acc[entry.entryId] = [...entry.picks].sort((a, b) => a.tierId - b.tierId);
        return acc;
      }, {} as Record<string, PlayerPoolMember[]>);
    },
  });

  const isLoading =
    isLoadingContestSlates || isLoadingEntriesForCurrentUser || isLoadingEntryToPicks;
  const isError = isErrorContestSlates || isErrorEntriesForCurrentUser || isErrorEntryToPicks;
  const entries =
    isLoading || isError
      ? undefined
      : entriesForCurrentUser.data?.map((entry) => ({
          ...entry,
          picks: entriesToPicks[entry.id],
        }));

  return {
    isLoading,
    isError,
    entries,
    slateId,
    slateStatus,
    entriesForCurrentUser,
  };
};

export default useMyContestEntries;
